<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { vIntersectionObserver } from "@vueuse/components";
import VSkeletonProductDetailsPromo from "@magnit/unit-product-details/src/components/skeleton/VSkeletonProductDetailsPromo.vue";
import VProductDetails from "@magnit/unit-product-details/src/components/VProductDetails.vue";
import VBreadcrumbsWrapped from "~/components/VBreadcrumbs/VBreadcrumbsWrapped.vue";
import type { IBreadcrumbsItemProps } from "~/components/VBreadcrumbs/VBreadcrumbs.types";
import VPageBack from "~/components/VPageBack.vue";
import VWrapperLocal from "~/components/VWrapperLocal.vue";
import VProductShare from "~/containers/VProductShare/VProductShare.vue";
import { Routes } from "~/utils/routes";
import type { ICategory } from "~/typings/categories";
import { useCategoriesStore } from "~/store/categories";
import { HttpCodes } from "~/api/config";
import promoApi from "~/api/promoApi";
import useCreateError from "~/composables/useCreateError";
import { collectPromoProductViewPayload } from "~/utils/analytics";
import { installAppBannerOptions } from "~/constants/product";

definePageMeta({
  layout: "product",
});

const categoriesStore = useCategoriesStore();
const route = useRoute();
const { send } = useAnalytics();
const { requestPromotionsItem } = promoApi();

useHead({
  link: [
    {
      rel: "canonical",
      href: route.path,
    },
  ],
});

const promoId = computed(() => (route.params.product ?? "") as string);
const promoSlug = computed(() => (route.params.slug ?? "") as string);

const {
  data: product,
  pending,
  status,
} = await requestPromotionsItem(promoId.value);
if (status.value === "error") {
  useCreateError("Promo not found", HttpCodes.NotFound);
}
if (product.value?.slug && !promoSlug.value) {
  navigateTo({
    name: RouteNames.ProductPromo,
    params: { product: product.value.id, slug: product.value.slug },
    query: route.query,
  });
}

const currentCategory = computed<ICategory | null>(() =>
  categoriesStore.promo.length && product.value?.articleCategory
    ? categoriesStore.getCategoryById(product.value?.articleCategory, "promo")
    : null,
);

const breadcrumbs = computed<IBreadcrumbsItemProps[]>(() => {
  let steps: ICategory[] = [];
  if (currentCategory.value && categoriesStore.promo.length) {
    steps = categoriesStore.getParentsByKey(currentCategory.value.key);
    steps.push(currentCategory.value);
  }

  return [
    {
      to: Routes.Main,
      title: "Главная",
    },
    ...steps.map((step, index) => {
      const obj: IBreadcrumbsItemProps = {
        title: step.name,
      };

      if (index !== steps.length - 1) {
        obj.to = step.url;
      }

      return obj;
    }),
  ];
});

const onVisibilityCardDetail = ([
  { isIntersecting },
]: IntersectionObserverEntry[]) => {
  if (!isIntersecting || !product.value) return;
  send("CardDetail:View", collectPromoProductViewPayload(product.value));
};

const { metaTitle, metaDesc, metaKeywords } = useCatalogMeta(
  product.value?.name || currentCategory.value?.name || SEO_DEFAULT_TITLE,
);

useSeoMeta({
  title: metaTitle,
  description: metaDesc,
  keywords: metaKeywords,
});
</script>

<template>
  <VWrapperLocal>
    <VSkeletonProductDetailsPromo v-if="pending" />
  </VWrapperLocal>

  <div
    v-show="!pending"
    v-if="product"
    v-intersection-observer="onVisibilityCardDetail"
    class="promo-product"
  >
    <VBreadcrumbsWrapped :items="breadcrumbs" />

    <VWrapperLocal class="promo-product__navigation">
      <VPageBack
        v-if="currentCategory"
        :text="currentCategory.name"
        :route="currentCategory.url"
      />
      <VProductShare
        :id="product.id"
        :name="product.name"
        :discount-category="
          'discountCategory' in product ? product.discountCategory : ''
        "
        :category-key="currentCategory ? currentCategory.key : ''"
      />
    </VWrapperLocal>

    <VWrapperLocal>
      <VProductDetails
        :product="product"
        :category-name="currentCategory?.name || ''"
        :install-app-banner-options="installAppBannerOptions"
        @analytics="send"
      />
    </VWrapperLocal>
  </div>
</template>

<style lang="postcss" scoped>
.promo-product {
  &__loader {
    height: calc(100vh - 56px);
  }

  &__gallery {
    position: relative;
    margin-top: 12px;

    .promo-product__sale-period + & {
      margin-top: 18px;
    }
  }

  &__slide {
    text-align: center;

    & img {
      max-height: 296px;
    }
  }

  &__badges {
    display: flex;
    position: absolute;
    top: 3px;
    left: 0;

    .promo-product__gallery & {
      z-index: var(--pl-z-index-level2);
      left: 12px;
    }
  }

  &__navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  &__params {
    padding: 8px 0 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid var(--pl-divider-primary);
  }

  &__params-divider {
    height: var(--pl-unit-x2);
  }

  &__touch-title {
    margin: var(--pl-unit-x2) 0 var(--pl-unit-x3);
  }

  &__progressive-string {
    display: inline-flex;
    align-items: center;
    padding-top: 2px;
    gap: var(--pl-unit-x1);
  }

  &__sale {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;

    .promo-product__gallery & {
      z-index: var(--pl-z-index-level2);
      position: absolute;
      left: 12px;
      bottom: 0;
    }
  }

  &__sale-price {
    .promo-product__prices > & {
      margin-left: 8px;
      text-decoration: line-through;
    }
  }

  &__sale-percent {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 10px;
    height: 28px;
    min-width: 60px;
    font: var(--pl-font-body-large-accent);
    color: var(--pl-text-invert-primary);
    background: var(--pl-project-cosmetic-primary);
    border-radius: 20px 0;

    .promo-product__gallery & {
      border-radius: 0 20px;
    }
  }

  &__sale-sign {
    font: var(--pl-font-caption-large-regular);
  }

  &__description-title {
    margin-bottom: 8px;
  }

  &__progressive {
    background: var(--pl-surface-muted-default);
    padding: 4px;
    border-radius: 16px;
    display: flex;
    margin-top: 20px;
  }

  &__progressive-item {
    flex-grow: 1;
    cursor: pointer;
    text-align: center;
    font: var(--pl-font-body-large-regular);
    padding: 4px 16px;

    &_active {
      border-radius: 12px;
      background: var(--pl-surface-primary-default);
      font: var(--pl-font-body-large-accent);
      box-shadow: var(--pl-shadow-s);
    }
  }

  &__notice {
    margin-top: 16px;
    background: var(--pl-surface-muted-default);
    border-radius: 12px;
    padding: 16px;

    ul {
      list-style: disc inside;
    }

    li {
      text-indent: 5px;
    }
  }
}

@media (--pl-viewport-from-s) {
  .promo-product {
    &__badges,
    &__sale {
      .promo-product__gallery & {
        left: 20px;
      }
    }
  }
}

@media (--pl-viewport-from-m) {
  .promo-product {
    &__gallery {
      & img {
        max-height: 336px;
      }
    }

    &__badges,
    &__sale {
      .promo-product__gallery & {
        left: 32px;
      }
    }
  }
}

@media (--pl-viewport-from-l) {
  .promo-product {
    &__image {
      position: relative;
      border-radius: 16px;
      overflow-y: auto;
      padding: 8px;

      & img {
        width: 100%;
      }
    }

    &__params {
      margin-top: 24px;
      padding: 16px;
      border: 1px solid var(--pl-border-secondary-default);
      border-radius: 16px;
    }

    &__params-divider {
      height: var(--pl-unit-x4);
    }

    &__sale-wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__sale {
      position: static;
    }

    &__description-title {
      position: relative;
      display: inline-block;
      margin-bottom: 12px;

      &__wrapper {
        margin-bottom: 16px;
        border-bottom: 1px solid var(--pl-divider-primary);
      }

      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: -13px;
        height: 2px;
        width: 100%;
        background: #2a2a2a;
      }
    }

    &__progressive {
      margin-top: 32px;
    }

    &__notice {
      margin-top: 24px;
    }
  }
}

@media (--pl-viewport-from-l) {
  .promo-product {
    &__progressive {
      max-width: 400px;
    }
  }
}
</style>
